import { useState, useEffect } from "react";
import { TextInput, Button, Select, Stack, Box } from "@mantine/core";
import { DateTimePicker } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { WorkPriority, WorkStatus } from "../../gql/graphql";
import { Description } from "../../components/Description";

interface ProcessCycleFormProps {
  onSave: (values: any) => void;
  onCancel: () => void;
  onDelete?: () => void;
  initialState: "ADD" | "EDIT";
  selectedRow: any;
}

export const ProcessCycleForm: React.FC<ProcessCycleFormProps> = ({
  onSave,
  onCancel,
  onDelete,
  initialState,
  selectedRow,
}) => {
  const [isEditing] = useState(initialState === "EDIT");

  const form = useForm({
    initialValues: {
      id: null,
      title: "",
      priority: WorkPriority.Medium,
      description: "",
      status: WorkStatus.ToDo,
      deadline: "",
      deadline_date: new Date(),
      input: "",
    },
    validate: {
      title: (value: string) => (value ? null : "Please enter a title"),
      priority: (value: string) => (value ? null : "Please select a priority"),
    },
  });

  useEffect(() => {
    if (initialState === "EDIT" && selectedRow) {
      form.setValues({
        id: selectedRow.key,
        title: selectedRow.cycle,
        status: selectedRow.status,
        priority: selectedRow.priority,
        deadline: selectedRow.deadline,
        description: selectedRow.description || "",
        input: "",
      });
    }
  }, [initialState, selectedRow]);

  const handleSave = async () => {
    const validation = form.validate();
    if (!validation.hasErrors) {
      onSave(form.values);
      onCancel();
    }
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        handleSave();
      }}
    >
      <Stack gap="md">
        {/* <TextInput type="hidden" {...form.getInputProps("id")} />
        <TextInput type="hidden" {...form.getInputProps("status")} />
        <TextInput type="hidden" {...form.getInputProps("deadline")} /> */}

        <TextInput
          label="Title"
          placeholder="Enter title"
          required
          {...form.getInputProps("title")}
        />

        <Description
          markdown={initialState === "EDIT" ? selectedRow?.description : ""}
          onChange={(markdown: string) =>
            form.setFieldValue("description", markdown)
          }
          showToolbar={form.isDirty()}
          placeholder="Enter project description..."
        />

        {initialState === "ADD" && (
          <Description
            markdown=""
            onChange={(markdown: string) =>
              form.setFieldValue("input", markdown)
            }
            showToolbar={form.isDirty()}
            placeholder="Enter input data..."
          />
        )}

        <Select
          label="Default Priority"
          placeholder="Select priority"
          required
          data={[
            { value: "Low", label: "Low" },
            { value: "Medium", label: "Medium" },
            { value: "High", label: "High" },
            { value: "Critical", label: "Critical" },
          ]}
          defaultValue="Medium"
          {...form.getInputProps("priority")}
          styles={{ dropdown: { zIndex: 20000 } }}
        />

        <DateTimePicker
          label="Deadline"
          placeholder="Select deadline"
          valueFormat="YYYY-MM-DD HH:mm"
          {...form.getInputProps("deadline_date")}
          onChange={(date) => {
            if (date) {
              form.setFieldValue("deadline_date", date);
              form.setFieldValue("deadline", date.toISOString());
            }
          }}
        />
        <Box style={{ display: "flex", gap: "8px" }}>
          <Button
            variant="outline"
            onClick={onCancel}
            // disabled={form.isDirty() === false}
          >
            {isEditing ? "Undo" : "Cancel"}
          </Button>
          {initialState === "EDIT" && onDelete && (
            <Button
              variant="filled"
              color="red"
              onClick={onDelete}
              style={{ marginLeft: "auto" }}
            >
              Delete
            </Button>
          )}
          <Button
            type="submit"
            variant="filled"
            disabled={form.isDirty() === false}
            style={{
              marginLeft: initialState === "EDIT" && onDelete ? "8px" : "auto",
            }}
          >
            {isEditing ? "Save Changes" : "Add Project"}
          </Button>
        </Box>
      </Stack>
    </form>
  );
};

export default ProcessCycleForm;
